import React, { useState, useEffect, useCallback } from 'react';
import { useAlert } from './AlertContext';
import { useConfirmation } from './ConfirmationContext';
import { CustomInput, CustomSelect, CustomSlider, CustomToggle } from './CustomFormElements';
import Button from './Buttons';
import Table from './Tables';
import { useChannelApi } from '../Services/ChannelApiProvider';
import { isEmpty } from '../utils/validationUtil';

function GuardrailSettings({ channelId, canEdit, initialData, onClose, onUpdate }) {
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const {
    createGuardrail,
    updateGuardrail,
    getThematics,
    addGuardrailThematic,
    removeGuardrailThematic,
    updateGuardrailThematicConfidence,
  } = useChannelApi();

  const isNew = !initialData.id;

  const [name, setName] = useState('');
  const [action, setAction] = useState('deactivate_forever');
  const [isActive, setIsActive] = useState(true);
  const [allowedThematics, setAllowedThematics] = useState([]);
  const [thematics, setThematics] = useState([]);

  // For adding new thematic
  const [selectedThematic, setSelectedThematic] = useState(null);
  const [confidenceLevel, setConfidenceLevel] = useState(80);
  const [showAddThematic, setShowAddThematic] = useState(false);

  useEffect(() => {
    setName(initialData.name || '');
    setAction(initialData.action || 'deactivate_forever');
    setIsActive(initialData.is_active ?? true);
    setAllowedThematics(initialData.allowed_thematics || []);
  }, [initialData]);

  useEffect(() => {
    const fetchThematics = async () => {
      try {
        const res = await getThematics();
        setThematics(res?.data || res || []);
      } catch (err) {
        console.error('Failed to fetch thematics:', err);
      }
    };
    fetchThematics();
  }, [getThematics]);

  const handleSave = async () => {
    if (isEmpty(name)) {
      showAlert('fail', 'Please enter a name for the guardrail', 'error');
      return;
    }

    const payload = {
      name: name,
      action: action,
      is_active: isActive,
    };

    try {
      if (isNew) {
        await createGuardrail(channelId, payload);
        showAlert('success', 'Guardrail created successfully');
      } else {
        await updateGuardrail(channelId, initialData.id, payload);
        showAlert('success', 'Guardrail updated successfully');
      }
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to save guardrail:', error);
      showAlert('fail', 'Failed to save guardrail.');
    }
  };

  const handleAddThematic = async () => {
    if (!selectedThematic) {
      showAlert('fail', 'Please select a thematic', 'error');
      return;
    }

    try {
      const result = await addGuardrailThematic(
        channelId,
        initialData.id,
        selectedThematic,
        confidenceLevel,
      );
      setAllowedThematics(result.allowed_thematics || []);
      setShowAddThematic(false);
      setSelectedThematic(null);
      setConfidenceLevel(80);
      showAlert('success', 'Thematic added successfully');
    } catch (error) {
      console.error('Failed to add thematic:', error);
      showAlert('fail', 'Failed to add thematic.');
    }
  };

  const handleRemoveThematic = (thematicKey) => {
    showConfirmation('Are you sure you want to remove this thematic?', async () => {
      try {
        const result = await removeGuardrailThematic(channelId, initialData.id, thematicKey);
        setAllowedThematics(result.allowed_thematics || []);
        showAlert('success', 'Thematic removed successfully');
      } catch (error) {
        console.error('Failed to remove thematic:', error);
        showAlert('fail', 'Failed to remove thematic.');
      }
    });
  };

  const thematicColumns = [
    { name: 'Thematic', selector: (row) => row.thematic_name, sortable: false },
    { name: 'Confidence Level', selector: (row) => `${row.confidence_level}%`, sortable: false },
  ];

  // Filter out already added thematics
  const availableThematics = thematics.filter(
    (t) => !allowedThematics.some((at) => at.id === t.key),
  );

  return (
    <div className="d-flex flex-column h-100">
      <h2 className="h4-title">{isNew ? 'Create New Guardrail' : 'Edit Guardrail'}</h2>

      <div style={{ flexGrow: 1, paddingTop: '1rem' }}>
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Guardrail Name</div>
            <div className="small-font">Enter a name for this guardrail</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomInput
              id="guardrail-name"
              label="Name"
              placeholder="e.g., 'Sales Topics Only'"
              value={name}
              onChange={(id, value) => setName(value)}
              disabled={!canEdit}
            />
          </div>
        </div>

        <div className="input-block d-flex flex-row justify-between" style={{ display: 'none' }}>
          <div className="input-description">
            <div className="input-title">Action</div>
            <div className="small-font">Action to take when thematic is not allowed</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomSelect
              id="guardrail-action"
              label="Action"
              options={[{ value: 'deactivate_forever', label: 'Deactivate Forever' }]}
              value={action}
              onChange={(val) => setAction(val)}
              multiSelect={false}
              disabled={!canEdit}
            />
          </div>
        </div>

        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Active</div>
            <div className="small-font">Enable or disable this guardrail</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomToggle
              id="guardrail-active"
              checked={isActive}
              onChange={() => setIsActive(!isActive)}
              disabled={!canEdit}
            />
          </div>
        </div>

        {!isNew && (
          <>
            <h3 className="h4-title" style={{ marginTop: '2rem' }}>
              Allowed Thematics
            </h3>
            <p className="small-font">
              Configure which thematics are allowed to pass through this guardrail
            </p>

            <Table
              title="Allowed Thematics"
              columns={thematicColumns}
              data={allowedThematics}
              onDelete={(key) => handleRemoveThematic(key)}
              selectableRows={canEdit}
              selectableRowsHighlight
            />

            {canEdit && !showAddThematic && (
              <div className="d-flex justify-start" style={{ marginTop: '1rem' }}>
                <Button size="medium" variant="secondary" onClick={() => setShowAddThematic(true)}>
                  Add Thematic
                </Button>
              </div>
            )}

            {showAddThematic && (
              <div className="border-yes radius-yes p-4" style={{ marginTop: '1rem' }}>
                <h4 className="h4-title">Add New Thematic</h4>

                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Thematic</div>
                    <div className="small-font">Select a thematic to allow</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSelect
                      id="thematic-select"
                      label="Thematic"
                      options={availableThematics.map((t) => ({
                        value: t.key,
                        label: t.name,
                      }))}
                      value={selectedThematic}
                      multiSelect={false}
                      onChange={(val) => setSelectedThematic(val)}
                    />
                  </div>
                </div>

                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Confidence Level</div>
                    <div className="small-font">Minimum confidence level required (0-100)</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSlider
                      id="confidence-slider"
                      label="Confidence Level"
                      min={0}
                      max={100}
                      oneStep={5}
                      suffix=" %"
                      value={confidenceLevel}
                      onChange={(val) => setConfidenceLevel(val)}
                    />
                  </div>
                </div>

                <div className="d-flex justify-end gap-2" style={{ marginTop: '1rem' }}>
                  <Button
                    size="medium"
                    variant="danger"
                    onClick={() => {
                      setShowAddThematic(false);
                      setSelectedThematic(null);
                      setConfidenceLevel(80);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button size="medium" variant="primary" onClick={handleAddThematic}>
                    Add
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="buttons-container d-flex justify-end" style={{ marginTop: '2rem' }}>
        <Button variant="primary" size="medium" disabled={!canEdit} onClick={handleSave}>
          {isNew ? 'Create' : 'Save'}
        </Button>
        <Button variant="danger" size="medium" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default GuardrailSettings;
